import { FC, useRef, useState, useEffect } from 'react'
import { Typography } from '@mui/material'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { closeModal, modalPropsSelector } from 'store/modals/modalSlice'
import { BenchmarkingDetailsWrapper } from './benchmarkingDetails.styles'

const formatPercent = (val: number | null | undefined): string => {
  if (val == null) return '-'
  return Number.isInteger(val) ? `${val}%` : `${parseFloat(val.toFixed(1))}%`
}

const BenchmarkingDetails: FC = () => {
  const dispatch = useAppDispatch()
  const modalProps = useAppSelector(modalPropsSelector)
  const data = modalProps?.props?.benchmarkingDetailsData

  const cpis = data?.cpis || []
  const lastUpdate = data?.lastUpdate || ''

  const lessColRef = useRef<HTMLTableCellElement>(null)
  const sameColRef = useRef<HTMLTableCellElement>(null)
  const moreColRef = useRef<HTMLTableCellElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [lessLeft, setLessLeft] = useState<number | null>(null)
  const [sameLeft, setSameLeft] = useState<number | null>(null)
  const [moreLeft, setMoreLeft] = useState<number | null>(null)

  useEffect(() => {
    if (wrapperRef.current) {
      const wrapperRect = wrapperRef.current.getBoundingClientRect()
      if (lessColRef.current) {
        setLessLeft(lessColRef.current.getBoundingClientRect().left - wrapperRect.left)
      }
      if (sameColRef.current) {
        setSameLeft(sameColRef.current.getBoundingClientRect().left - wrapperRect.left)
      }
      if (moreColRef.current) {
        setMoreLeft(moreColRef.current.getBoundingClientRect().left - wrapperRect.left)
      }
    }
  }, [cpis])

  const handleClose = () => {
    dispatch(closeModal())
  }

  /*
   * The category split the bar shows, passed in rather than rebuilt here. Re-deriving it by summing
   * the per-CPI percentages weighted every CPI equally - ignoring the weight column this very table
   * prints - so the footer disagreed with the bar above it.
   * */
  const summary = data?.summary
  const totalLess = cpis.reduce((sum, cpi) => sum + (cpi.summary?.less ?? 0), 0)
  const totalEqual = cpis.reduce((sum, cpi) => sum + (cpi.summary?.equal ?? 0), 0)
  const totalMore = cpis.reduce((sum, cpi) => sum + (cpi.summary?.more ?? 0), 0)
  const total = totalLess + totalEqual + totalMore
  const lessPercent = summary ? summary.less : total > 0 ? (totalLess / total) * 100 : 0
  const equalPercent = summary ? summary.equal : total > 0 ? (totalEqual / total) * 100 : 0
  const morePercent = summary ? summary.more : total > 0 ? (totalMore / total) * 100 : 0

  return (
    <BenchmarkingDetailsWrapper ref={wrapperRef}>
      <div className="modal-header">
        <Typography className="header-title">Calculation details</Typography>
        <svg
          className="close-button"
          onClick={handleClose}
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14 26L26 14M14 14L26 26"
            stroke="#E9E0E4"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <div className="content-header">
        <span>
          Number of rows: <span className="highlight">{cpis.length}</span>
        </span>
        <span>
          Last Update: <span className="highlight">{lastUpdate}</span>
        </span>
      </div>

      <div className="modal-content">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>CPI</th>
                <th>Weight</th>
                <th ref={lessColRef}>Less Strict SLA</th>
                <th ref={sameColRef}>Same SLA</th>
                <th ref={moreColRef}>More Strict SLA</th>
              </tr>
            </thead>
            <tbody>
              {cpis.map((cpi, index) => (
                <tr key={cpi.cpi_name || index}>
                  <td>
                    <div className="cpi-cell">
                      <div className="cpi-chip">
                        <span>{cpi.cpi_display_name}</span>
                      </div>
                      <span className="cpi-name">{cpi.cpi_title}</span>
                    </div>
                  </td>
                  <td>{cpi.cpi_weight != null ? `${cpi.cpi_weight}%` : '-'}</td>
                  <td>
                    <span className="status-chip less-strict">{formatPercent(cpi.summary?.less)}</span>
                  </td>
                  <td>
                    <span className="status-chip same-sla">{formatPercent(cpi.summary?.equal)}</span>
                  </td>
                  <td>
                    <span className="status-chip more-strict">{formatPercent(cpi.summary?.more)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="modal-footer">
        <Typography className="result-label">Result Score:</Typography>
        {lessLeft != null && (
          <Typography className="result-value" style={{ position: 'absolute', left: lessLeft + 16 }}>
            {formatPercent(lessPercent)}
          </Typography>
        )}
        {sameLeft != null && (
          <Typography className="result-value" style={{ position: 'absolute', left: sameLeft + 16 }}>
            {formatPercent(equalPercent)}
          </Typography>
        )}
        {moreLeft != null && (
          <Typography className="result-value" style={{ position: 'absolute', left: moreLeft + 16 }}>
            {formatPercent(morePercent)}
          </Typography>
        )}
      </div>
    </BenchmarkingDetailsWrapper>
  )
}

export default BenchmarkingDetails
